import { TextButton } from "@/components/ui/pressable";
import { useState } from "react";
import { useFormContext, useWatch } from "react-hook-form";
import { required } from "ra-core";
import { Plus, X } from "lucide-react";
import { DateInput } from "@/components/admin/date-input";
import { TaskDescriptionMentionInput } from "@/components/atomic-crm/tasks/TaskDescriptionMentionInput";
import { CalendarReminderOffsetsInput } from "@/modules/calendar/CalendarReminderOffsetsInput";
import { CalendarTimeInput } from "@/modules/calendar/CalendarTimeInput";
import { TeamMemberMultiSelect } from "@/modules/shared/TeamMemberMultiSelect";
import { cn } from "@/lib/utils";

const dateKeyOf = (value: unknown) => String(value ?? "").slice(0, 10);

const startNotAfterDue = (value: unknown, values: Record<string, unknown>) => {
  const start = dateKeyOf(value);
  const due = dateKeyOf(values.due_date);
  if (!start || !due) return undefined;
  return start > due
    ? "Start date must be on or before the due date"
    : undefined;
};

/** Date row: optional start date (multi-day task), due date and time. */
const TaskDateFields = () => {
  const { setValue } = useFormContext();
  const startDate = useWatch({ name: "start_date" });
  const [showStart, setShowStart] = useState(() => Boolean(startDate));
  const hasStart = showStart || Boolean(startDate);

  return (
    <div className="space-y-1.5">
      <div
        className={cn(
          "grid grid-cols-1 gap-4",
          hasStart ? "sm:grid-cols-3" : "sm:grid-cols-2",
        )}
      >
        {hasStart ? (
          <DateInput
            source="start_date"
            label="Start date"
            helperText={false}
            validate={startNotAfterDue}
          />
        ) : null}
        <DateInput
          source="due_date"
          label={hasStart ? "Due date" : "Date"}
          helperText={false}
          validate={required()}
        />
        <CalendarTimeInput source="due_time" label="Time" helperText={false} />
      </div>
      <TextButton
        tone="muted"
        size="xs"
        onClick={() => {
          if (hasStart) {
            setValue("start_date", null, { shouldDirty: true });
            setShowStart(false);
          } else {
            setShowStart(true);
          }
        }}
      >
        {hasStart ? <X className="size-3.5" /> : <Plus className="size-3.5" />}
        {hasStart ? "Remove start date" : "Start date"}
      </TextButton>
    </div>
  );
};

export const TaskCoreFormFields = ({
  defaultDealId,
  rows = 3,
  autoFocus = true,
}: {
  defaultDealId?: string | number | null;
  rows?: number;
  autoFocus?: boolean;
}) => (
  <>
    <TaskDescriptionMentionInput
      autoFocus={autoFocus}
      source="text"
      label="Description"
      validate={required()}
      className="m-0"
      defaultDealId={defaultDealId}
      rows={rows}
    />
    <TaskDateFields />
    <TeamMemberMultiSelect
      source="assignee_person_ids"
      label="Team members"
      required
    />
    <CalendarReminderOffsetsInput />
  </>
);
